/**
 * @group Network
 * @summary Mapping of network error codes to user-friendly messages
 * 
 * @remarks
 * This constant maps network error codes to their corresponding user-friendly messages,
 * providing a centralized way to translate technical error codes into readable messages.
 * 
 * @example
 * ```typescript
 * import { ERROR_CODE_MESSAGES, NETWORK_ERROR_CODES } from '@shared/constants';
 * 
 * function getErrorMessage(errorCode: string): string {
 *   return ERROR_CODE_MESSAGES[errorCode] || 'An unknown error occurred';
 * }
 * 
 * const message = getErrorMessage(NETWORK_ERROR_CODES.ECONNREFUSED);
 * ```
 */
export const ERROR_CODE_MESSAGES = {
  ECONNREFUSED: 'Connection was refused by the server',
  ECONNRESET: 'Connection was reset by the server',
  ECONNABORTED: 'Connection was reset by the server',
  ETIMEDOUT: 'Connection timed out while trying to reach the server',
  ENOTFOUND: 'Failed to resolve server address',
  UND_ERR_CONNECT_TIMEOUT: 'Connection timed out while trying to reach the server',
  UND_ERR_HEADERS_TIMEOUT: 'Response timed out',
  UND_ERR_BODY_TIMEOUT: 'Response timed out',
  FETCH_ERROR: 'Failed to establish connection to the server',
  ABORT_ERR: 'Request timed out',
  NETWORK_ERR: 'Failed to establish connection to the server',
  DB_CONNECTION_FAILED: 'Failed to connect to database',
  DB_QUERY_TIMEOUT: 'Database operation timed out',
  API_RATE_LIMITED: 'Too many requests. Please try again later',
} as const; 